import { useLanderConfig } from "../commons/ConfigContext";
import {
  isRollerAdsGeoOverride,
  isYahooApprovedMarket,
} from "../commons/HelperFunctions";
import { FeedType, LanderConfig } from "../types/LanderConfig";
import { buildRsocRedirectUrl } from "../rsoc/useRsoc";
import { useRollerAdsUsExperiment } from "../experimentation/useRollerAdsUsExperiment";
import { useLlmKeywordsExperiment } from "../experimentation/useLlmKeywordsExperiment";
import { Settings } from "../config";

export enum AdProvider {
  RSOC = "rsoc",
  ROLLER_ADS = "roller_ads",
  YAHOO = "yahoo",
}

export interface AdProviderResult {
  provider: AdProvider;
  rsocUrl: string | null;
  rsocError: boolean;
  experimentKeywordClick: boolean;
  yahooUnapprovedKeywordClick: boolean;
  /**
   * 1-click keyword clicks go to the second account (pid 387) instead of 343:
   * the RollerAds 1-click flow and, with the Yahoo provider, the RollerAds
   * backfill on the Yahoo ads page. The 0-click redirect always uses 309.
   */
  keywordClickAccount387: boolean;
}

function isRsocEligible(landerConfig: LanderConfig): boolean {
  const rsocPathOrUrlExists =
    !!landerConfig.rsoc?.path || !!landerConfig.rsoc?.url;
  const feedIDIsRsoc = landerConfig.lander?.feedID === FeedType.RSOC;
  const domainIsCloudynestStore =
    landerConfig.domain?.rootDomain === "cloudynest.store";

  return rsocPathOrUrlExists && feedIDIsRsoc && !domainIsCloudynestStore;
}

/**
 * Builds an AdProviderResult, defaulting every field the caller leaves out, so
 * each branch below is one statement naming only what it changes.
 */
function adProviderResult(
  provider: AdProvider,
  overrides: Partial<AdProviderResult> = {}
): AdProviderResult {
  return {
    provider,
    rsocUrl: null,
    rsocError: false,
    experimentKeywordClick: false,
    yahooUnapprovedKeywordClick: false,
    keywordClickAccount387: false,
    ...overrides,
  };
}

/** RollerAds serving 1-click keyword links instead of the 0-click redirect. */
function rollerAdsKeywordClick(): AdProviderResult {
  return adProviderResult(AdProvider.ROLLER_ADS, {
    experimentKeywordClick: true,
  });
}

/** RSOC when its redirect URL builds, otherwise the Yahoo error fallback. */
function resolveRsoc(landerConfig: LanderConfig): AdProviderResult {
  const { url } = buildRsocRedirectUrl(landerConfig);
  return url
    ? adProviderResult(AdProvider.RSOC, { rsocUrl: url })
    : adProviderResult(AdProvider.YAHOO, { rsocError: true });
}

/**
 * Resolves which ad provider to use. Priority order:
 * 1. Geo-override (CN + the keyword-click geos) -> ROLLER_ADS
 * 2. RSOC-eligible: treatment overrides to ROLLER_ADS 1-click; otherwise RSOC
 * 3. feedID === ROLLER_ADS_1CLICK -> ROLLER_ADS with experimentKeywordClick;
 *    ROLLER_ADS_1CLICK_387 -> the same, on the second 1-click account
 * 4. feedID === ROLLER_ADS -> ROLLER_ADS (unaffected by experiment)
 * 5. US experiment treatment -> ROLLER_ADS 1-click
 * 6. Market Yahoo is not approved to monetize -> ROLLER_ADS 1-click
 * 7. Yahoo baseline -> YAHOO
 */
function resolveAdProvider(
  landerConfig: LanderConfig,
  inRollerAdsTreatment: boolean
): AdProviderResult {
  const feedID = landerConfig.lander?.feedID;

  if (isRollerAdsGeoOverride()) return adProviderResult(AdProvider.ROLLER_ADS);

  if (isRsocEligible(landerConfig)) {
    return inRollerAdsTreatment
      ? rollerAdsKeywordClick()
      : resolveRsoc(landerConfig);
  }

  if (feedID === FeedType.ROLLER_ADS_1CLICK) return rollerAdsKeywordClick();
  if (feedID === FeedType.ROLLER_ADS_1CLICK_387) {
    return adProviderResult(AdProvider.ROLLER_ADS, {
      experimentKeywordClick: true,
      keywordClickAccount387: true,
    });
  }
  if (feedID === FeedType.ROLLER_ADS) {
    return adProviderResult(AdProvider.ROLLER_ADS);
  }
  if (inRollerAdsTreatment) return rollerAdsKeywordClick();
  if (!isYahooApprovedMarket()) {
    return adProviderResult(AdProvider.ROLLER_ADS, {
      yahooUnapprovedKeywordClick: true,
    });
  }

  return adProviderResult(AdProvider.YAHOO);
}

/**
 * The ad provider for this visitor (resolveAdProvider).
 *
 * PARKING-5307: with LLM_COHORT_ROLLER_ADS_387 on, the `llm` cohort of
 * parking_llm_keywords keeps the routing above and sends its RollerAds keyword
 * clicks to account 387, so that account's report carries the arm's 1-click
 * revenue; the control arm stays on 343. RSOC has no RollerAds clicks.
 */
export function useAdProvider(): AdProviderResult {
  const { landerConfig } = useLanderConfig();
  const { inRollerAdsTreatment } = useRollerAdsUsExperiment();
  const { inLlmKeywordsCohort } = useLlmKeywordsExperiment();
  const result = resolveAdProvider(landerConfig, inRollerAdsTreatment);

  if (
    Settings.LLM_COHORT_ROLLER_ADS_387 &&
    inLlmKeywordsCohort &&
    result.provider !== AdProvider.RSOC
  ) {
    return { ...result, keywordClickAccount387: true };
  }
  return result;
}
